import { localizeRouteKey } from "@/utils/common";
import { AppRoute } from "@/routes/app-routes";
import { Koulutusmuoto, Organisation } from "@/types";
import { IntlShape } from "react-intl";

/**
 * Muodostaa ja palauttaa polun (url-osoite) annettujen parametrien
 * perusteella. Polkua on tarkoitus käyttää Wizardin sulkemistoimintojen
 * yhteydessä siten, että käyttäjä ohjataan asianhallintasivun tilanteen-
 * mukaiseen näkymään.
 * @param role - Käyttäjän rooli.
 * @param locale - Käyttöliittymän kieli.
 * @param intl
 * @param organisaatio - Organisaatio, jonka oid-tietoa käytetään
 *                       muodostettavassa polussa
 * @param koulutusmuoto - Koulutusmuoto-tyyppinen objekti.
 * @param uuid - Muutospyynnön UUID (ei pakollinen tieto).
 * @returns URL-osoite.
 */
export const getUrlOnClose = (
  role: string,
  locale: string,
  intl: IntlShape,
  organisaatio: Organisation,
  koulutusmuoto: Koulutusmuoto,
  uuid?: string
): string => {
  // Jos käyttäjän rooli on KJ, palautetaan polku, jota pitkin
  // pääsee koulutusmuoto huomioiden käyttäjän oman organisaation
  // järjestämislupa-asioihin.
  // Oiva-daily August 6, 2026: Role "KJ" is most likely dead code left over from
  // removing a feature where education organizers could manage their permits.
  // The role is set in Asianhallinta/index.jsx and currently only the test user has the role "ESITTELIJA".
  // However, this code gets called through a WizardContainer, which in turn gets called by components
  // initiated through JarjestajaSwitch, where it is set to "KJ".
  // So, the role "KJ" is probably not used in the application anymore, but it is still set in some places.
  if (role === "KJ") {
    return localizeRouteKey(locale, AppRoute.Jarjestamislupaasiat, intl, {
      id: organisaatio.oid,
      koulutusmuoto: koulutusmuoto.kebabCase
    });
  } else {
    // Käyttäjän ollessa rooliltaan jokin muu kuin KJ, on kaksi
    // vaihtoehtoa: 1) Jos muutospyynnön UUID on käytettävissä eli
    // mikäli muutospyyntö on tallennettu, palautetaan polku, jota pitkin
    // pääsee Asian asiakirjat -näkymään kyseisen muutospyynnön kohdalle.
    if (uuid) {
      return `${localizeRouteKey(locale, AppRoute.Asia, intl, {
        koulutusmuoto: koulutusmuoto.kebabCase,
        uuid
      })}?force=true`;
    } else {
      // Mikäli käyttäjä ei ole tallentanut muutospyyntöä tai mikäli UUID ei
      // ole jostain muusta syystä käytettävissä, palautetaan avoimiin
      // asiakirjoihin johtava polku.
      return `${localizeRouteKey(locale, AppRoute.AsianhallintaAvoimet, intl, {
        koulutusmuoto: koulutusmuoto.kebabCase
      })}?force=true`;
    }
  }
};
